/**
 * Analytics.jsx - Historical stats, leaderboard, and player trends.
 *
 * Contains two main tabs:
 *   - Leaderboard: season-level fantasy point totals for all tracked players,
 *     filterable by position and searchable by name. Clicking a row jumps
 *     to the Trends tab for that player.
 *   - Player Trends: week-by-week fantasy point chart for a searched player,
 *     with the current ML projection overlaid as a future point.
 *
 * Covers 2015–2025 historical data; seasons without loaded data show a
 * "Coming Soon" placeholder instead of an empty table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import {
  RefreshCw, Search, X, Award, Activity,
  Lock, Users, LineChart as LineChartIcon
} from 'lucide-react';

const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE', 'K', 'DST'];
const SEASONS   = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015];

const POSITION_COLORS = {
  QB:  { bg: 'bg-red-500/20',    text: 'text-red-400',    border: 'border-red-500/30',    hex: '#f87171' },
  RB:  { bg: 'bg-green-500/20',  text: 'text-green-400',  border: 'border-green-500/30',  hex: '#4ade80' },
  WR:  { bg: 'bg-blue-500/20',   text: 'text-blue-400',   border: 'border-blue-500/30',   hex: '#60a5fa' },
  TE:  { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', hex: '#facc15' },
  K:   { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', hex: '#c084fc' },
  DST: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', hex: '#fb923c' },
};
const posStyle = (pos) => POSITION_COLORS[pos] || { bg: 'bg-white/10', text: 'text-white/60', border: 'border-white/10' };

/**
 * SVG line chart with an optional projection point plotted as a dashed
 * extension. Used in the Player Trends tab to overlay the ML prediction
 * on top of the historical game log.
 */
const LineChart = ({ data, color = '#4d7cff', projectionPoint = null }) => {
  if (!data || data.length < 2) return (
    <div className="h-[180px] flex items-center justify-center text-white/20 text-sm">Not enough data to chart</div>
  );
  const width = 900, height = 180;
  const padL = 40, padR = 20, padT = 12, padB = 28;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const allValues = data.map(d => d.value);
  if (projectionPoint != null) allValues.push(projectionPoint.value);
  const minV = Math.max(0, Math.min(...allValues) - 2);
  const maxV = Math.max(...allValues) + 2;
  const toX = i => data.length <= 1 ? padL + chartW / 2 : padL + (i / (data.length - 1)) * chartW;
  const toY = v => padT + chartH - ((v - minV) / (maxV - minV)) * chartH;
  const pathD = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(d.value).toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${toX(data.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)} L${toX(0).toFixed(1)},${(padT + chartH).toFixed(1)} Z`;
  const yTicks = [0, 0.5, 1].map(t => Math.round(minV + t * (maxV - minV)));
  const projX = projectionPoint != null ? Math.min(toX(data.length - 1) + 48, width - padR - 4) : null;
  const projY = projectionPoint != null ? toY(projectionPoint.value) : null;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 180 }}>
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={width - padR} y1={toY(v)} y2={toY(v)} stroke="rgba(255,255,255,0.04)" strokeWidth={1}/>
          <text x={padL - 5} y={toY(v) + 4} textAnchor="end" fontSize={9} fill="rgba(255,255,255,0.25)">{v}</text>
        </g>
      ))}
      <path d={areaD} fill={color} fillOpacity={0.06}/>
      <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={toX(i)} cy={toY(d.value)} r={2.5} fill={color}/>
          {/* Only label every Nth point so the axis doesn't get cluttered */}
          {(data.length <= 20 || i % Math.ceil(data.length / 10) === 0) && (
            <text x={toX(i)} y={height - 4} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.2)">{d.label}</text>
          )}
        </g>
      ))}
      {/* Projection dot — rendered as a dashed connector from the last historical point */}
      {projectionPoint != null && projX != null && (
        <g>
          <line x1={toX(data.length - 1)} y1={toY(data[data.length - 1].value)} x2={projX} y2={projY}
            stroke="#ffffff" strokeWidth={1.5} strokeDasharray="4 3" strokeOpacity={0.2}/>
          <circle cx={projX} cy={projY} r={8} fill="#4d7cff" fillOpacity={0.12}/>
          <circle cx={projX} cy={projY} r={4.5} fill="#4d7cff" stroke="#0d0d0d" strokeWidth={1.5}/>
          <text x={projX} y={projY - 11} textAnchor="middle" fontSize={9} fontWeight="bold" fill="#4d7cff">{projectionPoint.value.toFixed(1)}</text>
          <text x={projX} y={height - 4} textAnchor="middle" fontSize={8} fill="#4d7cff" fillOpacity={0.5}>{projectionPoint.label}</text>
        </g>
      )}
    </svg>
  );
};

// Circular player avatar — shows headshot or initials
const Avatar = ({ player, size = 8 }) => {
  const initials = player.player_name?.split(' ').map(n => n[0]).join('') || '?';
  const s = posStyle(player.position);
  const imgSrc = player.headshot_url ||
    (player.position === 'DST' && player.team_code && player.team_code !== '—'
      ? `https://a.espncdn.com/i/teamlogos/nfl/500/${player.team_code.toLowerCase()}.png`
      : null);
  return (
    <div className={`w-${size} h-${size} rounded-full ${s.bg} border ${s.border} overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${s.text}`}>
      {imgSrc
        ? <img src={imgSrc} alt={player.player_name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }}/>
        : initials}
    </div>
  );
};

// Shown in place of the leaderboard when the selected season has no data yet
const ComingSoonBanner = ({ season }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-4">
    <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
      <Lock size={24} className="text-white/25"/>
    </div>
    <div className="text-center">
      <h3 className="text-lg font-bold mb-1">{season} Data Coming Soon</h3>
      <p className="text-white/35 text-sm max-w-xs">Data for this season hasn't been loaded yet.</p>
    </div>
  </div>
);

// =============================================================================
// TAB 1 - LEADERBOARD
// =============================================================================
const LeaderboardTab = ({ season, availableSeasons, onPlayerClick }) => {
  const [position,       setPosition]       = useState('All');
  const [leaderboard,    setLeaderboard]     = useState([]);
  const [loading,        setLoading]         = useState(true);
  const [dataAvailable,  setDataAvailable]   = useState(true);
  const [searchQuery,    setSearchQuery]     = useState('');
  const [page,           setPage]            = useState(1);
  const [totalPages,     setTotalPages]      = useState(1);
  const [totalPlayers,   setTotalPlayers]    = useState(0);

  const fetchLeaderboard = useCallback(async (targetPage = 1, nameFilter = searchQuery) => {
    setLoading(true);
    try {
      const pos  = position === 'All' ? null : position;
      const data = await api.getLeaderboard({ season, position: pos, name: nameFilter || null, limit: 50, page: targetPage });
      setDataAvailable(data.data_available);
      setLeaderboard(data.players);
      setTotalPages(data.total_pages || 1);
      setTotalPlayers(data.total || 0);
    } catch {
      setLeaderboard([]);
      setDataAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [season, position]); // eslint-disable-line

  useEffect(() => { setPage(1); fetchLeaderboard(1, ''); }, [season, position]); // eslint-disable-line
  useEffect(() => { fetchLeaderboard(page); }, [page]); // eslint-disable-line

  // Debounced name search — hits the backend so players outside top-50 are found
  useEffect(() => {
    if (!searchQuery) { setPage(1); fetchLeaderboard(1, ''); return; }
    const timer = setTimeout(() => { setPage(1); fetchLeaderboard(1, searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line

  const seasonInfo  = availableSeasons.find(s => s.season === season);
  const isAvailable = !seasonInfo || seasonInfo.available;
  const displayed   = leaderboard;

  // Only show the stat columns relevant to the active position filter
  const showPassYds = !position || position === 'All' || position === 'QB';
  const showRushYds = !position || position === 'All' || ['RB', 'QB', 'WR'].includes(position);
  const showRecYds  = !position || position === 'All' || ['WR', 'TE', 'RB'].includes(position);
  const showRec     = !position || position === 'All' || ['WR', 'TE', 'RB'].includes(position);

  return (
    <div>
      {/* Filter controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-5 flex-wrap">
        <div className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5 flex-wrap">
          {POSITIONS.map(p => (
            <button key={p} onClick={() => setPosition(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${position === p ? 'bg-[#4d7cff] text-white' : 'text-white/40 hover:text-white'}`}>
              {p}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25"/>
          <input type="text" placeholder="Filter by name..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-white/[0.04] border border-white/[0.08] rounded-xl pl-8 pr-8 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#4d7cff]/40 w-full sm:w-44"/>
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/25 hover:text-white">
              <X size={11}/>
            </button>
          )}
        </div>
        <button onClick={() => fetchLeaderboard(page)} className="text-white/25 hover:text-white">
          <RefreshCw size={14}/>
        </button>
      </div>

      {/* Leaderboard table */}
      <div data-tour="leaderboard-table" className="bg-[#080d1a] border border-white/[0.08] rounded-2xl overflow-hidden">
        {!isAvailable ? <ComingSoonBanner season={season}/> : (
          <>
            {loading ? (
              <div className="space-y-px p-3">
                {Array.from({length:10}).map((_,i) => (
                  <div key={i} className="h-11 bg-white/[0.03] rounded-xl animate-pulse" style={{opacity:1-i*0.08}}/>
                ))}
              </div>
            ) : displayed.length === 0 ? (
              <div className="text-center py-16 text-white/20 text-sm">No players found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/[0.03] border-b border-white/[0.08]">
                      <th className="px-3 py-3 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold w-10">#</th>
                      <th className="px-3 py-3 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold">Player</th>
                      <th className="hidden sm:table-cell px-3 py-3 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold">Pos</th>
                      <th className="hidden sm:table-cell px-3 py-3 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold">Team</th>
                      <th className="hidden sm:table-cell px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">GP</th>
                      <th className="px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Total Pts</th>
                      <th className="px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Avg/Gm</th>
                      {showPassYds && <th className="hidden md:table-cell px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Pass Yds</th>}
                      {showRushYds && <th className="hidden md:table-cell px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Rush Yds</th>}
                      {showRecYds  && <th className="hidden md:table-cell px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Rec Yds</th>}
                      {showRec     && <th className="hidden md:table-cell px-3 py-3 text-right text-[10px] text-white/40 uppercase tracking-widest font-bold">Rec</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.03]">
                    {displayed.map((p, i) => {
                      const s    = posStyle(p.position);
                      const rank = (page - 1) * 50 + i + 1;
                      return (
                        <tr key={p.player_id} onClick={() => onPlayerClick?.(p)} className={`${i % 2 !== 0 ? 'bg-white/[0.01]' : ''} hover:bg-white/[0.03] transition-colors cursor-pointer`}>
                          <td className="px-3 py-3">
                            {/* Highlight the top 3 ranks in blue */}
                            <span className={`text-sm font-bold ${rank <= 3 ? 'text-[#4d7cff]' : 'text-white/20'}`}>{rank}</span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5">
                              <Avatar player={p} size={8}/>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-medium truncate">{p.player_name}</span>
                                <span className={`sm:hidden text-[10px] font-bold ${s.text}`}>{p.position} · {p.team_code}</span>
                              </div>
                            </div>
                          </td>
                          <td className="hidden sm:table-cell px-3 py-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${s.bg} ${s.text}`}>{p.position}</span>
                          </td>
                          <td className="hidden sm:table-cell px-3 py-3 text-xs text-white/40">{p.team_code}</td>
                          <td className="hidden sm:table-cell px-3 py-3 text-xs text-white/40 text-right">{p.games}</td>
                          <td className="px-3 py-3 text-right">
                            <span className="text-sm font-black text-[#4d7cff]">{p.total_fantasy_points}</span>
                          </td>
                          <td className="px-3 py-3 text-sm font-semibold text-right">{p.avg_fantasy_points}</td>
                          {showPassYds && <td className="hidden md:table-cell px-3 py-3 text-xs text-white/35 text-right">{p.avg_passing_yards ?? '—'}</td>}
                          {showRushYds && <td className="hidden md:table-cell px-3 py-3 text-xs text-white/35 text-right">{p.avg_rushing_yards ?? '—'}</td>}
                          {showRecYds  && <td className="hidden md:table-cell px-3 py-3 text-xs text-white/35 text-right">{p.avg_receiving_yards ?? '—'}</td>}
                          {showRec     && <td className="hidden md:table-cell px-3 py-3 text-xs text-white/35 text-right">{p.avg_receptions ?? '—'}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination controls */}
            {!loading && dataAvailable && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.05]">
                <span className="text-[11px] text-white/25">
                  {(page-1)*50+1}–{Math.min(page*50, totalPlayers)} of {totalPlayers} players
                </span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                    className="px-3 py-1.5 rounded-lg text-xs border border-white/[0.08] text-white/40 hover:text-white disabled:opacity-30 transition-all">←</button>
                  {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
                    // Smart page window: always show first, last, and pages near current
                    if (totalPages <= 7) return i + 1;
                    if (i === 0) return 1;
                    if (i === 6) return totalPages;
                    if (page <= 4) return i + 1;
                    if (page >= totalPages - 3) return totalPages - 6 + i;
                    return page - 3 + i;
                  }).map((p, i) => (
                    <button key={i} onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${page===p ? 'bg-[#4d7cff] text-white' : 'border border-white/[0.08] text-white/40 hover:text-white'}`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                    className="px-3 py-1.5 rounded-lg text-xs border border-white/[0.08] text-white/40 hover:text-white disabled:opacity-30 transition-all">→</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// TAB 2 - PLAYER TRENDS
// =============================================================================
const PlayerTrendsTab = ({ availableSeasons, latestPrediction, initialPlayer }) => {
  const [query,          setQuery]          = useState('');
  const [results,        setResults]        = useState([]);
  const [searching,      setSearching]      = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [history,        setHistory]        = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [projection,     setProjection]     = useState(null); // current week ML projection

  const seasonOptions  = availableSeasons.filter(s => s.available).slice(0, 4);
  const defaultSeasons = seasonOptions.slice(0, 2).map(s => s.season).join(',');
  const [selectedSeasons, setSelectedSeasons] = useState(defaultSeasons || '2023,2024');

  // If a player was passed in (e.g. from a leaderboard click), load them immediately
  useEffect(() => {
    if (initialPlayer) loadHistory(initialPlayer);
  }, [initialPlayer]); // eslint-disable-line

  // Debounced player search — wait 300ms after the last keystroke before hitting the API
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try { setResults(await api.searchPlayers(query)); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * Load historical game log for a player.
   * Also fires a parallel request for the current week's projection so
   * it can be overlaid on the chart as a future point.
   */
  const loadHistory = useCallback(async (player, seasons) => {
    setSelectedPlayer(player);
    setResults([]);
    setQuery('');
    setProjection(null);
    setLoadingHistory(true);
    try {
      const projFetch = latestPrediction
        ? api.getProjections({ season: latestPrediction.season, week: latestPrediction.week })
            .then(d => (d.players || []).find(p => p.id === player.player_id))
            .catch(() => null)
        : Promise.resolve(null);
      const [hist, projData] = await Promise.all([
        api.getPlayerHistory(player.player_id, seasons || selectedSeasons),
        projFetch,
      ]);
      setHistory(hist);
      if (projData?.proj != null) setProjection({ value: projData.proj, label: `W${latestPrediction.week}`, ciLow: projData.ciLow, ciHigh: projData.ciHigh });
    } catch { setHistory(null); }
    finally { setLoadingHistory(false); }
  }, [selectedSeasons]); // eslint-disable-line

  const handleSeasonToggle = s => {
    setSelectedSeasons(s);
    if (selectedPlayer) loadHistory(selectedPlayer, s);
  };

  const chartData = history?.stats.map(s => ({ label: `${s.season} W${s.week}`, value: s.fantasy_points })) || [];
  const totalPts  = history?.stats.reduce((s,r) => s + r.fantasy_points, 0) || 0;
  const avgPts    = history?.stats.length ? totalPts / history.stats.length : 0;
  const bestWeek  = history?.stats.length ? Math.max(...history.stats.map(r => r.fantasy_points)) : 0;
  const style     = selectedPlayer ? posStyle(selectedPlayer.position) : null;

  return (
    <div>
      {/* Search + season range controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25"/>
          <input type="text" placeholder="Search any player..." value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-9 pr-8 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#4d7cff]/40"/>
          {query && (
            <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white">
              <X size={13}/>
            </button>
          )}
          {/* Autocomplete dropdown */}
          {(results.length > 0 || searching) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-white/[0.08] rounded-xl shadow-2xl z-50 overflow-hidden max-h-60 overflow-y-auto">
              {searching && <div className="px-4 py-3 text-xs text-white/25 flex items-center gap-2"><RefreshCw size={11} className="animate-spin"/> Searching...</div>}
              {results.map(r => {
                const s = posStyle(r.position);
                return (
                  <button key={r.player_id} onClick={() => loadHistory(r)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left transition-colors border-b border-white/[0.04] last:border-0">
                    <Avatar player={r} size={8}/>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{r.player_name}</div>
                      <div className="text-xs text-white/35">{r.team_code}</div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${s.bg} ${s.text}`}>{r.position}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Season range selector — shows available seasons as toggle buttons */}
        {seasonOptions.length > 0 && (
          <div className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5">
            {[
              { label: `${seasonOptions[0]?.season}`, value: `${seasonOptions[0]?.season}` },
              seasonOptions[1] && { label: `${seasonOptions[1]?.season}`, value: `${seasonOptions[1]?.season}` },
              seasonOptions[0] && seasonOptions[1] && { label: `${seasonOptions[1]?.season}+${seasonOptions[0]?.season}`, value: `${seasonOptions[1]?.season},${seasonOptions[0]?.season}` },
              seasonOptions.length >= 3 && { label: 'Last 3', value: seasonOptions.slice(0,3).map(s=>s.season).join(',') },
            ].filter(Boolean).map(opt => (
              <button key={opt.value} onClick={() => handleSeasonToggle(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${selectedSeasons === opt.value ? 'bg-[#4d7cff] text-white' : 'text-white/40 hover:text-white'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Empty state */}
      {!selectedPlayer && (
        <div className="flex flex-col items-center justify-center py-24 gap-4 border border-dashed border-white/[0.06] rounded-2xl">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
            <Search size={22} className="text-white/20"/>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-white/30 mb-1">Search for a player to view their trend</div>
            <div className="text-xs text-white/15">e.g. Mahomes, Jefferson, McCaffrey, Kelce</div>
          </div>
        </div>
      )}

      {loadingHistory && (
        <div className="flex items-center justify-center py-24 gap-2.5 text-white/25">
          <RefreshCw size={16} className="animate-spin"/> Loading history...
        </div>
      )}

      {selectedPlayer && history && !loadingHistory && (
        <div className="space-y-4">
          {/* Player summary header */}
          <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <Avatar player={history} size={12}/>
                <div>
                  <div className="font-bold text-base">{history.player_name}</div>
                  <div className="text-xs text-white/35">{history.position} · {history.team_code} · {history.stats.length} games shown</div>
                </div>
              </div>
              <div className="flex gap-6 flex-wrap">
                {[
                  { label: 'Total Pts',    value: totalPts.toFixed(1),  color: style?.text },
                  { label: 'Avg/Game',     value: avgPts.toFixed(1) },
                  { label: 'Best Week',   value: bestWeek.toFixed(1) },
                  ...(projection ? [{ label: `Wk ${latestPrediction?.week} Proj`, value: projection.value.toFixed(1), color: 'text-[#4d7cff]' }] : []),
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-xl font-black ${s.color || 'text-white'}`}>{s.value}</div>
                    <div className="text-[10px] text-white/25 uppercase tracking-wider whitespace-nowrap">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Line chart */}
          <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl p-5">
            <div className="flex items-center gap-4 mb-4 text-xs text-white/25">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 rounded" style={{background: style?.hex || '#4d7cff'}}></div>
                Historical
              </div>
              {projection && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#4d7cff]"></div>
                  Week {latestPrediction?.week} Projection
                  {projection.ciLow != null && (
                    <span className="text-white/15">({projection.ciLow.toFixed(1)}–{projection.ciHigh.toFixed(1)})</span>
                  )}
                </div>
              )}
            </div>
            <LineChart data={chartData} color={style?.hex || '#4d7cff'} projectionPoint={projection}/>
          </div>

          {/* Week-by-week breakdown table */}
          {history.stats.length > 0 && (
            <div className="bg-[#080d1a] border border-white/[0.08] rounded-2xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-white/[0.08] flex items-center justify-between">
                <div className="text-sm font-bold">Week-by-Week Breakdown</div>
                <div className="text-xs text-white/25">{history.stats.length} games</div>
              </div>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full min-w-[400px]">
                  <thead className="sticky top-0 bg-[#080d1a]">
                    <tr className="bg-white/[0.03] border-b border-white/[0.08]">
                      {['Season','Week','Pts','Pass Yds','Rush Yds','Rec Yds','Rec'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-[10px] text-white/40 uppercase tracking-widest font-bold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.03]">
                    {/* Show most recent games first */}
                    {[...history.stats].reverse().map((s, i) => (
                      <tr key={i} className={`${i % 2 !== 0 ? 'bg-white/[0.01]' : ''} hover:bg-white/[0.03] transition-colors`}>
                        <td className="px-3 py-2.5 text-xs text-white/50">{s.season}</td>
                        <td className="px-3 py-2.5 text-xs text-white/50">Wk {s.week}</td>
                        <td className="px-3 py-2.5 text-sm font-bold" style={{color: style?.hex || '#4d7cff'}}>{s.fantasy_points.toFixed(1)}</td>
                        <td className="px-3 py-2.5 text-xs text-white/50">{s.passing_yards ?? '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-white/50">{s.rushing_yards ?? '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-white/50">{s.receiving_yards ?? '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-white/50">{s.receptions ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedPlayer && !history && !loadingHistory && (
        <div className="flex items-center justify-center py-20 text-white/20 text-sm">
          No historical data found for this player in the selected seasons
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================
const Analytics = () => {
  const location = useLocation();
  const [activeTab,        setActiveTab]       = useState('leaderboard');
  const [initialPlayer,    setInitialPlayer]   = useState(null);

  // If navigated here from a Dashboard player card, jump straight to the trends tab
  useEffect(() => {
    const incoming = location.state?.player;
    if (incoming) {
      setInitialPlayer(incoming);
      setActiveTab('trends');
    }
  }, []); // eslint-disable-line

  const [season,           setSeason]          = useState(2025);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [latestPrediction, setLatestPrediction] = useState(null);

  // Top scorer and total player count for the header summary cards
  const [topPlayer,    setTopPlayer]    = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Demo tour event — switch to trends and auto-load the top player
  useEffect(() => {
    const handler = () => {
      if (topPlayer) {
        setInitialPlayer(topPlayer);
        setActiveTab('trends');
      }
    };
    window.addEventListener('demo:showTrends', handler);
    return () => window.removeEventListener('demo:showTrends', handler);
  }, [topPlayer]);

  useEffect(() => {
    // Load which seasons have data, then auto-select the most recent available one
    api.getSeasonAvailability().then(data => {
      setAvailableSeasons(data);
      const latest = data.filter(s => s.available).sort((a, b) => b.season - a.season)[0];
      if (latest) setSeason(latest.season);
    }).catch(() => {});

    // Also grab the latest prediction week for the trend chart overlay
    api.getAvailableWeeks().then(weeks => {
      if (weeks?.length) {
        const sorted = [...weeks].sort((a, b) => b.season !== a.season ? b.season - a.season : b.week - a.week);
        setLatestPrediction(sorted[0]);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setSummaryLoading(true);
    api.getLeaderboard({ season, limit: 1, page: 1 })
      .then(d => { setTopPlayer(d.players?.[0] || null); setTotalPlayers(d.total || 0); })
      .catch(() => { setTopPlayer(null); setTotalPlayers(0); })
      .finally(() => setSummaryLoading(false));
  }, [season]);

  const tabs = [
    { id: 'leaderboard', label: 'Leaderboard',   icon: <Users size={14}/> },
    { id: 'trends',      label: 'Player Trends', icon: <LineChartIcon size={14}/> },
  ];

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans pb-16">
      <Navbar/>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-7 gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">Analytics</h1>
            <p className="text-white/30 text-sm">Historical performance, model accuracy & player trends</p>
          </div>
          {/* Season selector — locks icon on unavailable seasons */}
          <div data-tour="season-selector" className="flex items-center gap-2">
            <span className="text-white/30 text-xs font-medium">Season</span>
            <div className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5">
              {SEASONS.map(s => {
                const info  = availableSeasons.find(a => a.season === s);
                const avail = !info || info.available;
                return (
                  <button key={s} onClick={() => setSeason(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${season===s ? 'bg-[#4d7cff] text-white' : avail ? 'text-white/40 hover:text-white' : 'text-white/20'}`}>
                    {s}{!avail && <Lock size={8} className="opacity-50"/>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Summary stat cards */}
        <div data-tour="analytics-cards" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7">
          {[
            { icon: <Users size={16}/>,      label: `Players Tracked (${season})`,    value: summaryLoading ? null : totalPlayers,                                                             color: 'text-blue-400',   border: 'border-blue-500/20',   glow:'bg-blue-500'   },
            { icon: <Award size={16}/>,       label: `Top Scorer (${season})`,         value: summaryLoading ? null : (topPlayer ? `${topPlayer.player_name} (${topPlayer.total_fantasy_points})` : '—'), color: 'text-indigo-400', border: 'border-indigo-500/20', glow:'bg-indigo-500' },
            { icon: <Activity size={16}/>,    label: 'Historical Data',                value: (() => { const y = availableSeasons.filter(s => s.available).map(s => s.season); return y.length > 1 ? `${Math.min(...y)} – ${Math.max(...y)}` : y.length === 1 ? String(y[0]) : '—'; })(),  color: 'text-green-400',  border: 'border-green-500/20',  glow:'bg-green-500'  },
          ].map((s, i) => (
            <div key={i} className={`bg-[#0c1526] border ${s.border} rounded-2xl p-5 relative overflow-hidden`}>
              <div className={`absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-10 blur-2xl ${s.glow}`}/>
              <div className={`mb-2.5 ${s.color}`}>{s.icon}</div>
              {s.value === null
                ? <div className="h-7 w-28 bg-white/[0.05] rounded-lg animate-pulse mb-1.5"/>
                : <div className={`text-xl font-bold mb-1 ${s.color} truncate`}>{s.value}</div>}
              <div className="text-white/30 text-[11px] font-medium uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab switcher */}
        <div data-tour="trends-tab" className="flex gap-1 bg-white/[0.04] p-1 rounded-2xl mb-6 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#4d7cff] text-white shadow-[0_2px_8px_rgba(77,124,255,0.3)]'
                  : 'text-white/40 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div>
          {activeTab === 'leaderboard' && (
            // Clicking a leaderboard row jumps to trends for that player
            <LeaderboardTab season={season} availableSeasons={availableSeasons} onPlayerClick={p => { setInitialPlayer(p); setActiveTab('trends'); }}/>
          )}
          {activeTab === 'trends' && (
            <PlayerTrendsTab availableSeasons={availableSeasons} latestPrediction={latestPrediction} initialPlayer={initialPlayer}/>
          )}
        </div>

      </main>
    </div>
  );
};

export default Analytics;
